import useCreateLabourForecast from '@/api/mutations/labourForecast/useCreateLabourForecast';
import useUpdateLabourForecast from '@/api/mutations/labourForecast/useUpdateLabourForecast';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { LabourForecast } from '@/gql/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  CurrencyInput,
  Field,
  Form,
  InputGroup,
  RawInput,
  Sidebar,
} from '@payaca/ambrosia';

import { FC, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { z } from 'zod/v4';

const FORM_SCHEMA = z.object({
  description: z.string().min(1, 'Description is required'),
  costs: z.object({
    timeUnit: z.enum(['HOUR', 'DAY']),
    costPerTimeUnit: z.number().min(0),
    quantity: z.number().min(0.001),
    totalCost: z.number().min(0),
  }),
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  labourForecast?: Pick<
    LabourForecast,
    'id' | 'costPerTimeUnit' | 'description' | 'quantity' | 'timeUnit'
  >;
};

const getDefaultValues = (labourForecast: Props['labourForecast']) => {
  return labourForecast
    ? {
        description: labourForecast.description,
        costs: {
          costPerTimeUnit: labourForecast.costPerTimeUnit?.value || 0,
          quantity: labourForecast.quantity,
          totalCost: Math.ceil(
            labourForecast.costPerTimeUnit.value * labourForecast.quantity
          ),
          timeUnit: labourForecast.timeUnit,
        },
      }
    : {
        costs: {
          timeUnit: 'HOUR' as const,
          totalCost: 0,
        },
      };
};

export const CreateEditLabourForecastSidebar: FC<Props> = (props) => {
  const { isOpen, onClose, labourForecast } = props;

  const isEditing = !!labourForecast;

  const [isDirty, setIsDirty] = useState(false);

  return (
    <Sidebar
      title={`${isEditing ? 'Update' : 'Add'} forecast cost`}
      isOpen={isOpen}
      onClose={onClose}
      warnUserOnClose={isDirty}
    >
      <SidebarContent
        labourForecast={labourForecast}
        onClose={onClose}
        setIsDirty={setIsDirty}
      />
    </Sidebar>
  );
};

const SidebarContent: FC<{
  labourForecast?: Pick<
    LabourForecast,
    'id' | 'costPerTimeUnit' | 'description' | 'quantity' | 'timeUnit'
  >;
  onClose: () => void;
  setIsDirty: (isDirty: boolean) => void;
}> = (props) => {
  const { onClose, labourForecast, setIsDirty } = props;

  const { projectId } = useParams<{ projectId: string }>();
  const { data: regionPreferences } = useGetMyRegionPreferences();

  const isEditing = !!labourForecast;

  const {
    mutateAsync: createLabourForecast,
    isPending: isCreating,
    error: createError,
  } = useCreateLabourForecast(projectId);

  const {
    mutateAsync: updateLabourForecast,
    isPending: isUpdating,
    error: updateError,
  } = useUpdateLabourForecast(projectId);

  const isPersisting = isCreating || isUpdating;

  const formMethods = useForm<z.infer<typeof FORM_SCHEMA>>({
    defaultValues: getDefaultValues(labourForecast),
    resolver: zodResolver(FORM_SCHEMA),
  });

  useEffect(() => {
    setIsDirty(formMethods.formState.isDirty);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'setIsDirty'
  }, [formMethods.formState.isDirty]);

  const onSubmit: SubmitHandler<z.infer<typeof FORM_SCHEMA>> = async (data) => {
    if (isEditing) {
      await updateLabourForecast({
        id: labourForecast.id,
        description: data.description,
        costPerTimeUnit: data.costs.costPerTimeUnit,
        quantity: data.costs.quantity,
        timeUnit: data.costs.timeUnit,
      }).then(onClose);
    } else {
      await createLabourForecast({
        projectId: projectId,
        description: data.description,
        costPerTimeUnit: data.costs.costPerTimeUnit,
        quantity: data.costs.quantity,
        timeUnit: data.costs.timeUnit,
      }).then(onClose);
    }
  };

  return (
    <Form
      methods={formMethods}
      onSubmit={onSubmit}
      className="flex flex-col h-full"
    >
      <Sidebar.Body className="p-4 space-y-4">
        <Field name={'description'}>
          <Field.Label>Description</Field.Label>
          <RawInput {...formMethods.register('description')} />
        </Field>
        <Controller
          name="costs"
          render={({ field: { name, value: costsValue, onChange } }) => {
            const handleChange =
              (key: keyof typeof costsValue) => (newValue: any) => {
                const newCostsValue = {
                  ...costsValue,
                };

                if (key === 'quantity') {
                  const parsed = parseFloat(newValue);
                  newCostsValue.quantity = isNaN(parsed) ? undefined : parsed;
                }

                if (key === 'costPerTimeUnit') {
                  newCostsValue.costPerTimeUnit = newValue;
                }

                if (key === 'timeUnit') {
                  newCostsValue.timeUnit = newValue;
                }

                newCostsValue.totalCost = Math.ceil(
                  (newCostsValue.quantity || 0) *
                    (newCostsValue.costPerTimeUnit || 0)
                );

                onChange(newCostsValue);
              };

            return (
              <>
                <Field name="costs.quantity">
                  <Field.Label>Time</Field.Label>
                  <InputGroup>
                    <InputGroup.Input
                      value={costsValue.quantity}
                      onChange={handleChange('quantity')}
                      // @ts-expect-error `type="number"` is disallowed on Input — use NumberInput instead. Legacy usage.
                      type="number"
                      step="any"
                      min="0"
                    />

                    <InputGroup.Select
                      className="max-w-24"
                      value={costsValue.timeUnit}
                      onChange={handleChange('timeUnit')}
                      options={[
                        {
                          label: 'hours',
                          value: 'HOUR',
                        },
                        {
                          label: 'days',
                          value: 'DAY',
                        },
                      ]}
                    />
                  </InputGroup>
                </Field>
                <Field name={'costs.costPerTimeUnit'}>
                  <Field.Label>Cost per unit</Field.Label>
                  <CurrencyInput
                    value={costsValue.costPerTimeUnit}
                    onChange={handleChange('costPerTimeUnit')}
                    currency={regionPreferences?.currency}
                  >
                    <CurrencyInput.Suffix>
                      per {costsValue.timeUnit.toLowerCase()}
                    </CurrencyInput.Suffix>
                  </CurrencyInput>
                </Field>
                <Field name={'costs.totalCost'}>
                  <Field.Label>Cost</Field.Label>
                  <CurrencyInput
                    readOnly={true}
                    value={costsValue.totalCost}
                    currency={regionPreferences?.currency}
                  />
                </Field>
              </>
            );
          }}
        />
      </Sidebar.Body>
      <Sidebar.Footer className="space-y-4">
        {!!createError && (
          <div>
            <Alert colour="red" iconName="alert-triangle.3">
              {"Something went wrong - we couldn't record your labour forecast"}
            </Alert>
          </div>
        )}
        {!!updateError && (
          <div>
            <Alert colour="red" iconName="alert-triangle.3">
              {"Something went wrong - we couldn't update your labour forecast"}
            </Alert>
          </div>
        )}
        <div className="flex gap-x-4 justify-stretch">
          <Button onClick={onClose} variant="secondary" className="w-full">
            Cancel
          </Button>
          <Button
            className="w-full"
            type="submit"
            isProcessing={isPersisting}
            disabled={isPersisting}
          >
            {isEditing ? 'Save changes' : 'Add'}
          </Button>
        </div>
      </Sidebar.Footer>
    </Form>
  );
};
